import { ILeadContract } from '../entities/ILeadContract';
import { ISignatureRequest } from '../entities/ISignatureRequest';
import { ContractVariableSourceEnum } from '../enums/ContractVariableSourceEnum';
import { httpClient } from './helper/httpClient';
import {
  IPresignedPostTarget,
  uploadFileToPresignedPost,
} from './helper/presignedPostUpload';

export interface ICreateSignatureRequestParams {
  signerName: string;
  signerEmail?: string;
  signerDocument?: string;
}

export const SIGNED_CONTRACT_CONTENT_TYPE = 'application/pdf';
export const SIGNED_CONTRACT_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const ISSUE_TIMEOUT_MS = 95000;

export type ContractFileType = 'docx' | 'pdf' | 'signed';

export interface IIssueContractParams {
  templateId: string;
  contractorId: string;
  saleId?: string;
  issuanceCity?: string;
  customValues?: Record<string, string>;
}

export interface IContractMissingVariable {
  key: string;
  label: string;
  source: ContractVariableSourceEnum;
}

export interface IContractPreview {
  resolved: Record<string, string>;
  missing: IContractMissingVariable[];
}

export interface IIssuedContract extends ILeadContract {
  pdfGenerated: boolean;
}

export interface IContractDownloadUrl {
  downloadUrl: string;
}

class LeadContractService {
  private buildBaseURL(leadId: string): string {
    return `leads/${leadId}/contracts`;
  }

  preview(leadId: string, data: IIssueContractParams): Promise<IContractPreview> {
    return httpClient.post<null, IContractPreview>(
      `${this.buildBaseURL(leadId)}/preview`,
      { ...data },
    );
  }

  issue(leadId: string, data: IIssueContractParams): Promise<IIssuedContract> {
    return httpClient.post<null, IIssuedContract>(
      this.buildBaseURL(leadId),
      { ...data },
      { timeout: ISSUE_TIMEOUT_MS },
    );
  }

  getByLead(leadId: string): Promise<ILeadContract[]> {
    return httpClient.get<null, ILeadContract[]>(this.buildBaseURL(leadId));
  }

  remove(leadId: string, contractId: string): Promise<void> {
    return httpClient.delete(`${this.buildBaseURL(leadId)}/${contractId}`);
  }

  getDownloadUrl(
    leadId: string,
    contractId: string,
    type: ContractFileType,
  ): Promise<IContractDownloadUrl> {
    return httpClient.get<null, IContractDownloadUrl>(
      `${this.buildBaseURL(leadId)}/${contractId}/download-url`,
      { params: { type } },
    );
  }

  getSignedUploadUrl(
    leadId: string,
    contractId: string,
  ): Promise<IPresignedPostTarget> {
    return httpClient.post<null, IPresignedPostTarget>(
      `${this.buildBaseURL(leadId)}/${contractId}/signed-upload-url`,
    );
  }

  confirmSigned(leadId: string, contractId: string): Promise<ILeadContract> {
    return httpClient.post<null, ILeadContract>(
      `${this.buildBaseURL(leadId)}/${contractId}/confirm-signed`,
    );
  }

  uploadSignedFileWithProgress(
    target: Pick<IPresignedPostTarget, 'uploadUrl' | 'fields'>,
    file: File,
    onProgress: (percent: number) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    return uploadFileToPresignedPost(target, file, onProgress, signal);
  }

  private buildSignatureRequestsURL(leadId: string, contractId: string): string {
    return `${this.buildBaseURL(leadId)}/${contractId}/signature-requests`;
  }

  createSignatureRequest(
    leadId: string,
    contractId: string,
    data: ICreateSignatureRequestParams,
  ): Promise<ISignatureRequest> {
    return httpClient.post<ICreateSignatureRequestParams, ISignatureRequest>(
      this.buildSignatureRequestsURL(leadId, contractId),
      { ...data },
    );
  }

  getSignatureRequests(
    leadId: string,
    contractId: string,
  ): Promise<ISignatureRequest[]> {
    return httpClient.get<null, ISignatureRequest[]>(
      this.buildSignatureRequestsURL(leadId, contractId),
    );
  }

  cancelSignatureRequest(
    leadId: string,
    contractId: string,
    requestId: string,
  ): Promise<void> {
    return httpClient.delete(
      `${this.buildSignatureRequestsURL(leadId, contractId)}/${requestId}`,
    );
  }
}

export default new LeadContractService();
